import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Button, Intent, Menu, MenuItem, Position} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {Popover2} from '@blueprintjs/popover2';

import {ApiStatus, WorkspaceConfigShowCreateOptions} from '../../models';
import {reportIcon} from '../../utils/custom-icons';
import {useCurrentUser} from '../../hooks';
import {useShowCreateOptions, DEFAULT_SHOW_CREATE_OPTIONS} from '../../hooks/use-workspace-config';
import {isAdmin, isGuest} from '../../utils/auth';
import {IState} from '../../store/reducers';
import {REPORT_TYPE} from './constants';
import {isAnalytixLive, isPremise} from '../../utils/premise';

const iconProps: any = {
  width: 16,
  height: 16,
}

interface NewReportButtonProps {
  onClick?: (value: any) => void;
}

interface MenuContentProps {
  onClick: (value: any) => void;
  showCreateOptions: WorkspaceConfigShowCreateOptions;
}

const MenuContent = ({
  onClick,
  showCreateOptions,
}: MenuContentProps) => {
  return (
    <Menu>
      {
        showCreateOptions.report && <MenuItem
          text="Report"
          icon={reportIcon(iconProps)}
          onClick={() => onClick({type: REPORT_TYPE.REPORT})}
        />
      }
      {
        showCreateOptions.notebook && <MenuItem
          text="Notebook"
          icon={IconNames.MANUAL}
          onClick={() => onClick({type: REPORT_TYPE.NOTEBOOK})}
        />
      }
      {
        isPremise() && isAnalytixLive() && showCreateOptions.tableau_embed && <MenuItem
          text="Tableau Dashboard Embed"
          icon={IconNames.DASHBOARD}
          onClick={() => onClick({type: REPORT_TYPE.TABLEAU_EMBED})}
        />
      }
    </Menu>
  );
};

const NewReportButton = ({
  onClick,
}: NewReportButtonProps) => {
  const [init, setInit] = useState(false);
  const loadingStatusWorkspace = useSelector((state: IState) => state.workspaces.loadingListStatus);
  const currentUser = useCurrentUser();
  const showCreateOptions = useShowCreateOptions();
  const isLoading = (loadingStatusWorkspace === ApiStatus.LOADING);
  const userIsAdmin = isAdmin(currentUser);
  const userIsGuest = isGuest(currentUser);
  const history = useHistory();

  useEffect(() => {
    if (init) {
      return;
    }

    if (!isLoading) {
      setInit(true);
    }
  }, [init, isLoading]);

  const onNew = (data?: any) => {
    const type = data.type || '';

    history.push(`/r/new/${type}`);
    onClick && onClick(data);
  };

  if (userIsGuest ||
    (
      !userIsAdmin &&
      !(showCreateOptions.report || showCreateOptions.notebook || showCreateOptions.tableau_embed)
    )) {
    /*
     * if user is not admin and all options are false, don't show component
     */
    return null;
  }

  return (
    <Popover2
      content={
        <MenuContent
          onClick={onNew}
          showCreateOptions={userIsAdmin ? DEFAULT_SHOW_CREATE_OPTIONS : showCreateOptions}
        />
      }
      position={Position.BOTTOM_LEFT}
      captureDismiss={true}>
        <Button
          className={"add-new-button" + (isLoading ? ' bp4-skeleton' : '') }
          icon={IconNames.PLUS}
          text="New"
          intent={Intent.PRIMARY}
        />
    </Popover2>
  )
}

export default NewReportButton;
